import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../utils/api';

// MCP connections: AI assistants (Claude) connected to Mitigate via OAuth.
// User-scoped — each connection acts as the user who granted it.

export interface MCPConnection {
  id: number;
  client_name: string;
  grant_type: 'oauth' | 'pat';
  scopes: string[];
  created_at: string;
  last_used_at: string | null;
  is_active: boolean;
  // Present on the admin listing only.
  user?: {
    id: number;
    username: string;
    email: string;
  };
}

interface DataEnvelope<T> {
  data: T;
}

// ── connect info ─────────────────────────────────────────────────────────────

export interface MCPConnectInfo {
  // Empty string when no MCP service is deployed for this environment.
  mcp_url: string;
  allow_ai_assistants: boolean;
}

export const useMCPConnectInfo = () => {
  return useQuery({
    queryKey: ['mcp-connect-info'],
    queryFn: async () => {
      const res = await api.request<DataEnvelope<MCPConnectInfo>>(
        '/api/mcp/connect-info'
      );
      return res.data;
    },
  });
};

// ── user self-service ────────────────────────────────────────────────────────

export const useMyMCPConnections = () => {
  return useQuery({
    queryKey: ['mcp-connections', 'mine'],
    queryFn: async () => {
      const res = await api.request<DataEnvelope<MCPConnection[]>>(
        '/api/mcp/connections'
      );
      return res.data;
    },
  });
};

export const useRevokeMyMCPConnection = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.request<DataEnvelope<{ revoked: number }>>(
        `/api/mcp/connections/${id}`,
        { method: 'DELETE' }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mcp-connections'] });
    },
  });
};

// ── tenant admin ─────────────────────────────────────────────────────────────

export const useAdminMCPConnections = () => {
  return useQuery({
    queryKey: ['mcp-connections', 'admin'],
    queryFn: async () => {
      const res = await api.request<DataEnvelope<MCPConnection[]>>(
        '/api/admin/mcp-connections'
      );
      return res.data;
    },
  });
};

export const useAdminRevokeMCPConnection = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.request<DataEnvelope<{ revoked: number }>>(
        `/api/admin/mcp-connections/${id}`,
        { method: 'DELETE' }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mcp-connections'] });
    },
  });
};

export const useAIAssistantsSetting = () => {
  return useQuery({
    queryKey: ['ai-assistants-setting'],
    queryFn: async () => {
      const res = await api.request<DataEnvelope<{ allow_ai_assistants: boolean }>>(
        '/api/admin/tenant/ai-assistants'
      );
      return res.data;
    },
  });
};

export const useUpdateAIAssistantsSetting = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (allow: boolean) =>
      api.request<DataEnvelope<{ allow_ai_assistants: boolean }>>(
        '/api/admin/tenant/ai-assistants',
        { method: 'PATCH', body: { allow_ai_assistants: allow } }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-assistants-setting'] });
    },
  });
};
